import { useState, useEffect, useRef } from "react";
import { attributeApi } from "../api/api";
import "./common.css";
import { useResizableColumns } from "../hooks/useResizableColumns";
import { usePagination }       from "../hooks/usePagination";      // ← NEW
import Pagination              from "../components/Pagination";    // ← NEW

/* ── Toast ── */
function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3200);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div className={`rv-toast rv-toast--${type}`}>
      <span className="rv-toast-icon">{type === "success" ? <CheckIcon /> : <AlertIcon />}</span>
      <span className="rv-toast-msg">{message}</span>
      <button className="rv-toast-close" onClick={onClose}><CloseIcon /></button>
    </div>
  );
}

/* ── Confirm Dialog ── */
function ConfirmDialog({ name, onConfirm, onCancel }) {
  return (
    <div className="rv-dialog-backdrop">
      <div className="rv-dialog">
        <div className="rv-dialog-icon"><TrashIcon /></div>
        <h3 className="rv-dialog-title">Delete Attribute?</h3>
        <p className="rv-dialog-desc">
          <strong>{name}</strong> will be permanently removed. This action cannot be undone.
        </p>
        <div className="rv-dialog-actions">
          <button className="rv-btn rv-btn--ghost" onClick={onCancel}>Cancel</button>
          <button className="rv-btn rv-btn--danger" onClick={onConfirm}>Delete</button>
        </div>
      </div>
    </div>
  );
}

/* ── Attribute Form Modal ── */
function AttributeModal({ initial, onSave, onClose, saving }) {
  const isEdit = !!initial?.attributeID;
  const [form, setForm] = useState({
    attributeName: initial?.attributeName ?? "",
    attributeType: initial?.attributeType ?? "",
    isActive:      initial?.isActive      ?? true,
  });
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.attributeName.trim()) e.attributeName = "Attribute name is required.";
    if (!form.attributeType.trim()) e.attributeType = "Attribute type is required.";
    return e;
  };

  const handleSubmit = () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    onSave({ ...initial, ...form });
  };

  return (
    <div className="rv-dialog-backdrop">
      <div className="rv-dialog rv-dialog--form">
        <div className="rv-modal-header">
          <div className="rv-modal-icon"><AttrIcon /></div>
          <div>
            <h3 className="rv-modal-title">{isEdit ? "Edit Attribute" : "New Attribute"}</h3>
            <p className="rv-modal-sub">{isEdit ? "Update the attribute details below." : "Fill in details to create a new attribute."}</p>
          </div>
          <button className="rv-modal-close" onClick={onClose}><CloseIcon /></button>
        </div>

        <div className="rv-modal-body">
          <div className="rv-field">
            <label className="rv-label">Attribute Name <span className="rv-required">*</span></label>
            <input
              className={`rv-input ${errors.attributeName ? "rv-input--err" : ""}`}
              placeholder="e.g. Colour, Metal, Karat"
              value={form.attributeName}
              onChange={e => { setForm(f => ({ ...f, attributeName: e.target.value })); setErrors(v => ({ ...v, attributeName: "" })); }}
            />
            {errors.attributeName && <p className="rv-field-err">{errors.attributeName}</p>}
          </div>

          <div className="rv-field">
            <label className="rv-label">Attribute Type <span className="rv-required">*</span></label>
            <div className="rv-select-wrap">
              <select
                className={`rv-select ${errors.attributeType ? "rv-input--err" : ""}`}
                value={form.attributeType}
                onChange={e => { setForm(f => ({ ...f, attributeType: e.target.value })); setErrors(v => ({ ...v, attributeType: "" })); }}
              >
                <option value="">Select type…</option>
                <option value="Varchar">Varchar</option>
                <option value="Integer">Integer</option>
                <option value="Decimal">Decimal</option>
                {/* <option value="Boolean">Boolean</option>
                <option value="Color">Color</option> */}
              </select>
              <span className="rv-select-arrow"><ChevronIcon /></span>
            </div>
            {errors.attributeType && <p className="rv-field-err">{errors.attributeType}</p>}
          </div>

          <div className="rv-field rv-field--row">
            <div>
              <label className="rv-label">Status</label>
              <p className="rv-field-hint">Whether this attribute is visible in products</p>
            </div>
            <button
              type="button"
              className={`rv-toggle ${form.isActive ? "rv-toggle--on" : ""}`}
              onClick={() => setForm(f => ({ ...f, isActive: !f.isActive }))}
            >
              <span className="rv-toggle-knob" />
            </button>
          </div>
        </div>

        <div className="rv-modal-footer">
          <button className="rv-btn rv-btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="rv-btn rv-btn--primary" onClick={handleSubmit} disabled={saving}>
            {saving ? <span className="rv-spinner" /> : null}
            {isEdit ? "Update" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   MAIN ATTRIBUTE PAGE
══════════════════════════════════════ */
export default function AttributePage() {
  const [attributes, setAttributes]   = useState([]);
  const [loading, setLoading]         = useState(true);
  const [search, setSearch]           = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  const [modal,   setModal]   = useState(null);
  const [saving,  setSaving]  = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [toast,   setToast]   = useState(null);

  /* ── Resizable columns ── */
  const tableRef    = useRef(null);
  const [tableReady, setTableReady] = useState(false);
  useResizableColumns(tableRef, tableReady, [50, 200, 140, 120, 100]);

  /* ── Fetch ── */
  const fetchAll = async () => {
    try {
      setLoading(true);
      setTableReady(false);
      const data = await attributeApi.getAll();
      setAttributes(Array.isArray(data) ? data : data?.data ?? []);
    } catch (err) {
      showToast(err.message || "Failed to load attributes.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => { if (!loading) setTableReady(true); }, [loading]);

  const showToast = (message, type = "success") => setToast({ message, type });

  /* ── Save ── */
  const handleSave = async (formData) => {
    setSaving(true);
    try {
      if (formData.attributeID) {
        await attributeApi.update(formData);
        showToast("Attribute updated successfully.");
      } else {
        await attributeApi.create(formData);
        showToast("Attribute created successfully.");
      }
      setModal(null);
      fetchAll();
    } catch (err) {
      showToast(err.message || "Something went wrong.", "error");
    } finally {
      setSaving(false);
    }
  };

  /* ── Delete ── */
  const handleDelete = async () => {
    if (!confirm) return;
    try {
      await attributeApi.remove(confirm.id);
      showToast("Attribute deleted.");
      setConfirm(null);
      fetchAll();
    } catch (err) {
      showToast(err.message || "Delete failed.", "error");
    }
  };

  /* ── Filtered list ── */
  const filtered = attributes.filter(a => {
    const matchSearch =
      a.attributeName?.toLowerCase().includes(search.toLowerCase()) ||
      a.attributeType?.toLowerCase().includes(search.toLowerCase());
    const matchStatus =
      filterStatus === "all" ||
      (filterStatus === "active"   &&  a.isActive) ||
      (filterStatus === "inactive" && !a.isActive);
    return matchSearch && matchStatus;
  });

  /* ── Pagination ── */                               // ← NEW BLOCK
  const {
    paged, page, totalPages, pageSize,
    setPage, setPageSize,
    pageStart, pageEnd, totalItems,
  } = usePagination(filtered, 10);

  return (
    <div className="rv-attr-page">

      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
      {confirm && (
        <ConfirmDialog
          name={confirm.name}
          onConfirm={handleDelete}
          onCancel={() => setConfirm(null)}
        />
      )}
      {modal && (
        <AttributeModal
          initial={modal.data}
          onSave={handleSave}
          onClose={() => setModal(null)}
          saving={saving}
        />
      )}

      {/* ── Topbar ── */}
      <div className="rv-attr-topbar">
        <div className="rv-attr-topbar-left">
          <h2 className="rv-attr-heading">Attributes</h2>
          <span className="rv-attr-count">{attributes.length} total</span>
        </div>
        <button className="rv-btn rv-btn--primary" onClick={() => setModal({ data: {} })}>
          <PlusIcon /> New Attribute
        </button>
      </div>

      {/* ── Filters ── */}
      <div className="rv-attr-filters">
        <div className="rv-attr-search">
          <SearchIcon />
          <input
            placeholder="Search by name or type…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }} // ← reset page on search
          />
          {search && (
            <button className="rv-attr-search-clear" onClick={() => { setSearch(""); setPage(1); }}>
              <CloseIcon />
            </button>
          )}
        </div>

        <div className="rv-filter-tabs">
          {["all", "active", "inactive"].map(s => (
            <button
              key={s}
              className={`rv-filter-tab ${filterStatus === s ? "rv-filter-tab--active" : ""}`}
              onClick={() => { setFilterStatus(s); setPage(1); }} // ← reset page on filter
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* ── Table card ── */}
      <div className="rv-card rv-attr-card">
        {loading ? (
          <div className="rv-attr-empty">
            <div className="rv-loader" />
            <p>Loading attributes…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="rv-attr-empty">
            <div className="rv-empty-icon"><AttrIcon /></div>
            <p className="rv-empty-title">No attributes found</p>
            <p className="rv-empty-sub">
              {search ? "Try a different search term." : "Create your first attribute to get started."}
            </p>
            {!search && (
              <button className="rv-btn rv-btn--primary rv-mt-12" onClick={() => setModal({ data: {} })}>
                <PlusIcon /> New Attribute
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="rv-table-wrap rv-attr-table-wrap">
              <table className="rv-table rv-attr-table" ref={tableRef}>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Attribute Name</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map((attr, i) => (   // ← `paged` instead of `filtered`
                    <tr key={attr.attributeID} className="rv-attr-row">
                      <td className="rv-attr-idx">{pageStart + i}</td>  {/* ← correct serial */}
                      <td>
                        <div className="rv-attr-name-cell">
                          <span className="rv-attr-name">{attr.attributeName}</span>
                        </div>
                      </td>
                      <td>
                        <span className="rv-type-badge">{attr.attributeType}</span>
                      </td>
                      <td>
                        <span className={`rv-status-pill ${attr.isActive ? "rv-status-pill--active" : "rv-status-pill--inactive"}`}>
                          <span className="rv-status-dot" />
                          {attr.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td>
                        <div className="rv-actions">
                          <button
                            className="rv-action-btn rv-action-btn--edit"
                            title="Edit"
                            onClick={() => setModal({ data: attr })}
                          >
                            <EditIcon />
                          </button>
                          <button
                            className="rv-action-btn rv-action-btn--del"
                            title="Delete"
                            onClick={() => setConfirm({ id: attr.attributeID, name: attr.attributeName })}
                          >
                            <TrashIcon />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* ── Pagination bar ── */}
            <Pagination                          // ← NEW
              page={page}
              totalPages={totalPages}
              pageSize={pageSize}
              setPage={setPage}
              setPageSize={setPageSize}
              pageStart={pageStart}
              pageEnd={pageEnd}
              totalItems={totalItems}
            />
          </>
        )}
      </div>

      {/* ── Resize hint ── */}
      {!loading && filtered.length > 0 && (
        <p className="rv-resize-hint">
          <GripIcon /> Drag column borders to resize
        </p>
      )}
    </div>
  );
}

/* ── Icons ── */
function PlusIcon()    { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>; }
function SearchIcon()  { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>; }
function EditIcon()    { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>; }
function TrashIcon()   { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>; }
function CloseIcon()   { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>; }
function CheckIcon()   { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>; }
function AlertIcon()   { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>; }
function ChevronIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg>; }
function AttrIcon()    { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M12 2H2v10l9.29 9.29a1 1 0 0 0 1.41 0l6.29-6.29a1 1 0 0 0 0-1.41L12 2z"/><circle cx="7" cy="7" r="1.5" fill="currentColor"/></svg>; }
function GripIcon()    { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" style={{width:13,height:13}}><circle cx="9" cy="5" r="1" fill="currentColor"/><circle cx="9" cy="12" r="1" fill="currentColor"/><circle cx="9" cy="19" r="1" fill="currentColor"/><circle cx="15" cy="5" r="1" fill="currentColor"/><circle cx="15" cy="12" r="1" fill="currentColor"/><circle cx="15" cy="19" r="1" fill="currentColor"/></svg>; }